import { useCallback, useEffect, useRef, useState } from "react";
import { post as commsPost } from "../../service/comms/commsApi";
import { setTicketViewing } from "../../service/comms/supportService";
import { useRoomChannel } from "./useRoomChannel";

/** How often to re-assert "I'm looking at this" while the ticket is open. */
const HEARTBEAT_MS = 12000;
/**
 * How long another agent's claim is trusted without a refresh. The server
 * expires its own state after ~30s; expiring slightly sooner here means the
 * strip clears rather than lingering after someone closes their laptop.
 */
const VIEWER_TTL_MS = 30000;

/**
 * Collision detection for an open support ticket: heartbeat that this agent is
 * looking at it, and report who else is.
 *
 * The signal is transient — nothing is persisted, it never reaches the bell
 * feed, and it expires on its own — so a stale claim always resolves itself.
 * Mirrors the room `/typing` call in shape and cadence.
 *
 * @param {string|null} roomId - Room id backing the ticket.
 * @param {string|number|null} viewerUserId - The current agent, filtered out of
 *   the returned list (the server echoes our own heartbeat back).
 * @returns {{ viewers: Array<{ userId: string, name: string|null }> }}
 */
export function useTicketViewing(roomId, viewerUserId) {
  // userId -> { name, at } for everyone else currently on this ticket.
  const [others, setOthers] = useState(() => new Map());

  const handleTicketViewing = useCallback(
    (data) => {
      const userId = data?.user_id ?? data?.userId;
      if (userId == null) return;
      const key = String(userId);
      if (viewerUserId != null && key === String(viewerUserId)) return;

      setOthers((prev) => {
        const next = new Map(prev);
        if (data.viewing === false) {
          next.delete(key);
        } else {
          next.set(key, { name: data.name || data.user_name || null, at: Date.now() });
        }
        return next;
      });
    },
    [viewerUserId]
  );

  useRoomChannel(roomId, { onTicketViewing: handleTicketViewing });

  // Heartbeat while the ticket is open and the tab is visible. A hidden tab
  // stops claiming the ticket, which is the whole point of the signal.
  const roomRef = useRef(roomId);
  roomRef.current = roomId;

  useEffect(() => {
    if (!roomId) return undefined;

    let stopped = false;
    // A 403 means the agent is not a participant of this room — true for any
    // ticket they haven't picked up. That answer will not change while the
    // ticket is open, so the heartbeat gives up instead of re-asking (and
    // re-logging) every HEARTBEAT_MS.
    let denied = false;
    let timer = null;

    const beat = (viewing) => {
      if (denied || (stopped && viewing)) return;
      setTicketViewing(commsPost, roomId, viewing).catch((err) => {
        if (err?.status === 403) {
          denied = true;
          if (timer) clearInterval(timer);
        }
        // Any other failure is best-effort: it only costs a stale "is viewing"
        // strip on someone else's screen, which expires on its own.
      });
    };

    beat(!document.hidden);
    timer = setInterval(() => beat(!document.hidden), HEARTBEAT_MS);
    const onVisibility = () => beat(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      beat(false);
    };
  }, [roomId]);

  // Switching tickets must not carry the previous ticket's viewers over.
  useEffect(() => {
    setOthers(new Map());
  }, [roomId]);

  // Drop claims nobody has refreshed — an agent who closed the tab without a
  // clean "viewing: false" would otherwise hold the ticket forever.
  useEffect(() => {
    const sweep = setInterval(() => {
      setOthers((prev) => {
        const cutoff = Date.now() - VIEWER_TTL_MS;
        const next = new Map(
          Array.from(prev.entries()).filter(([, v]) => v.at >= cutoff)
        );
        return next.size === prev.size ? prev : next;
      });
    }, HEARTBEAT_MS);
    return () => clearInterval(sweep);
  }, []);

  const viewers = Array.from(others.entries()).map(([userId, v]) => ({
    userId,
    name: v.name,
  }));

  return { viewers };
}
